import { useEffect, useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";

const ROLES = [
  { value: "account_admin", label: "Account Admin" },
  { value: "billing_admin", label: "Billing Admin" },
  { value: "manager", label: "Manager" },
  { value: "standard_user", label: "Standard User" },
  { value: "limited_user", label: "Limited User" },
  { value: "read_only", label: "Read-Only" },
];

function normalizeLocalPart(value: string): string {
  return value.trim().toLowerCase();
}

function isValidUsername(local: string): boolean {
  const u = normalizeLocalPart(local);
  if (!u || u.length > 64 || u.includes("@")) return false;
  if (u.startsWith(".") || u.endsWith(".") || u.includes("..")) return false;
  return /^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?$/.test(u);
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountDomain: string;
  onSuccess: () => void;
};

export function CreateUserDialog({ open, onOpenChange, accountDomain, onSuccess }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("standard_user");
  const [jobTitle, setJobTitle] = useState("");
  const [inviteDeliveryEmail, setInviteDeliveryEmail] = useState("");

  useEffect(() => {
    if (!open) return;
    setUsername("");
    setFirstName("");
    setLastName("");
    setRole("standard_user");
    setJobTitle("");
    setInviteDeliveryEmail("");
  }, [open]);

  const composedEmail =
    username.trim() && accountDomain ? `${normalizeLocalPart(username)}@${accountDomain}` : "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const local = normalizeLocalPart(username);
    if (!isValidUsername(local)) {
      toast.error("Enter a valid username (local-part only, no @).");
      return;
    }
    if (!accountDomain) {
      toast.error("Organization domain is missing.");
      return;
    }
    const email = `${local}@${accountDomain.toLowerCase()}`;
    setSubmitting(true);
    try {
      const response = await fetch(apiUrl("/api/users"), {
        method: "POST",
        ...apiFetchInit(),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: email,
          email,
          name: firstName || lastName ? `${firstName} ${lastName}`.trim() : null,
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          accountDomain: accountDomain.toLowerCase(),
          role,
          isActive: true,
          jobTitle: jobTitle.trim() || null,
          appEntitlements: [],
          inviteDeliveryEmail: inviteDeliveryEmail.trim().toLowerCase() || undefined,
        }),
      });
      const data = (await response.json()) as { error?: string; inviteError?: string | null };
      if (!response.ok) {
        toast.error(data.error || "Could not create person.");
        return;
      }
      if (data.inviteError) {
        toast.error(`Person created, but invite failed: ${data.inviteError}`);
      } else {
        toast.success("Person created. Invite email sent when configured.");
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      toast.error("Could not create person.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="size-5" aria-hidden />
            Add person
          </DialogTitle>
          <DialogDescription>
            Creates an org login on <span className="font-mono">{accountDomain || "your domain"}</span> via{" "}
            <code className="text-xs">POST /api/users</code>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cu-username" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Username
              </Label>
              <Input
                id="cu-username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/@/g, ""))}
                placeholder="jane.doe"
                required
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Login email:{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {composedEmail || `username@${accountDomain || "domain"}`}
                </span>
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-first" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                First name
              </Label>
              <Input id="cu-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-last" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Last name
              </Label>
              <Input id="cu-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cu-job" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Job title
              </Label>
              <Input id="cu-job" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-role" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Role
              </Label>
              <select
                id="cu-role"
                className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-white/20 dark:bg-[#12161d]"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-invite" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Send invite to (optional)
              </Label>
              <Input
                id="cu-invite"
                type="email"
                value={inviteDeliveryEmail}
                onChange={(e) => setInviteDeliveryEmail(e.target.value)}
                placeholder="personal@example.com"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Creating…
                </>
              ) : (
                "Create and invite"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
